import React, { useEffect, useState, useCallback } from "react";
import { Radar, Play, Trash2, Plus, Check, Database, Store } from "lucide-react";
import { api, apiError } from "../api";
import { Spinner, StatusBadge } from "./ui";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtDateTime(iso) {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export const AdminMasterScans = ({ show }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  // add form
  const [name, setName] = useState("");
  const [catalogue, setCatalogue] = useState("retail");
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);

  const load = useCallback(() => {
    api.get("/admin/master-profiles").then((r) => setData(r.data)).catch((e) => show(apiError(e), "error"));
  }, [show]);

  useEffect(() => { load(); }, [load]);

  const runNow = async (mp) => {
    setBusy(`run-${mp.id}`);
    try {
      const { data: res } = await api.post(`/admin/master-profiles/${mp.id}/run`);
      show(res.message);
      setTimeout(load, 1500);
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  const toggleEnabled = async (mp) => {
    setBusy(`tog-${mp.id}`);
    try {
      await api.put(`/admin/master-profiles/${mp.id}`, { enabled: !mp.enabled });
      load();
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  const remove = async (mp) => {
    if (!window.confirm(`Delete master profile "${mp.name}"? Its schedule stops; stored prices remain.`)) return;
    setBusy(`del-${mp.id}`);
    try {
      await api.delete(`/admin/master-profiles/${mp.id}`);
      show("Master profile deleted.");
      load();
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  const toggleDay = (d) =>
    setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));

  const create = async () => {
    if (!name.trim()) { show("Enter a name.", "error"); return; }
    setBusy("create");
    try {
      await api.post("/admin/master-profiles", {
        name, catalogue, scan_hour: hour, scan_minute: minute, weekdays,
      });
      show("Master profile created.");
      setName(""); setShowAdd(false); setWeekdays([0, 1, 2, 3, 4, 5, 6]); setHour(6); setMinute(0); setCatalogue("retail");
      load();
    } catch (e) { show(apiError(e), "error"); } finally { setBusy(""); }
  };

  return (
    <div className="card p-5 mb-4" data-testid="admin-master-scans">
      <div className="flex items-center gap-2 mb-4">
        <Radar className="w-5 h-5 text-teal" />
        <h2 className="font-display text-lg text-ink">Master scans</h2>
      </div>
      <p className="text-sm text-muted mb-4">
        Master profiles scrape the full government catalogue and store the latest prices centrally.
        Every user profile scan then reads from this store instead of scraping — keeping daily scans fast at scale.
      </p>

      {/* Catalogue store status */}
      {data?.store && (
        <div className="grid grid-cols-2 gap-3 mb-5">
          {Object.entries(data.store).map(([cat, s]) => (
            <div key={cat} className="rounded-card border border-stone-border p-3" data-testid={`store-${cat}`}>
              <div className="flex items-center gap-1.5 text-xs text-muted mb-1"><Database className="w-3.5 h-3.5" /> {cat} store</div>
              <div className="font-display text-xl font-bold text-ink">{s.count.toLocaleString()} <span className="text-sm font-normal text-muted">prices</span></div>
              <div className="text-xs text-muted mt-0.5">updated {fmtDateTime(s.updated_at)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Master profile list */}
      {!data ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : data.master_profiles.length === 0 ? (
        <p className="text-sm text-muted">No master profiles. Add one below.</p>
      ) : (
        <div className="divide-y divide-stone-border mb-4">
          {data.master_profiles.map((mp) => (
            <div key={mp.id} className="py-3" data-testid={`master-row-${mp.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-ink font-medium truncate">{mp.name}</span>
                    <span className="chip bg-teal-light text-teal">{mp.catalogue_label}</span>
                    {mp.last_status && <StatusBadge status={mp.last_status === "success" ? "success" : mp.last_status === "running" ? "running" : "failed"} />}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {mp.weekdays.length === 7 ? "Daily" : mp.weekdays.map((d) => WEEKDAYS[d]).join(", ")} · {String(mp.scan_hour).padStart(2, "0")}:{String(mp.scan_minute).padStart(2, "0")} Mauritius
                  </div>
                  <div className="text-xs text-muted mt-0.5">
                    Last run {fmtDateTime(mp.last_finished_at)}
                    {mp.last_status === "success" && ` · ${mp.last_products?.toLocaleString?.() || 0} products · ${mp.last_changed || 0} changed`}
                    {mp.last_error && <span className="text-coral"> · {mp.last_error}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    className={`btn-ghost text-xs px-2.5 py-1.5 ${mp.enabled ? "text-emerald" : "text-muted"}`}
                    onClick={() => toggleEnabled(mp)}
                    disabled={busy === `tog-${mp.id}`}
                    data-testid={`master-toggle-${mp.id}`}
                  >
                    {mp.enabled ? "Enabled" : "Disabled"}
                  </button>
                  <button
                    className="btn-ghost text-xs px-2.5 py-1.5"
                    onClick={() => runNow(mp)}
                    disabled={busy === `run-${mp.id}` || mp.last_status === "running"}
                    data-testid={`master-run-${mp.id}`}
                  >
                    {busy === `run-${mp.id}` ? <Spinner /> : <Play className="w-3.5 h-3.5" />} Run now
                  </button>
                  <button
                    className="btn-ghost text-xs px-2 py-1.5 text-coral hover:bg-coral-light"
                    onClick={() => remove(mp)}
                    disabled={busy === `del-${mp.id}`}
                    aria-label="Delete master profile"
                    data-testid={`master-delete-${mp.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add master profile */}
      {!showAdd ? (
        <button className="btn-ghost w-full" onClick={() => setShowAdd(true)} data-testid="master-add-open">
          <Plus className="w-4 h-4" /> Add master profile
        </button>
      ) : (
        <div className="rounded-card border border-stone-border p-4 space-y-4" data-testid="master-add-form">
          <div>
            <label className="label" htmlFor="mname">Name</label>
            <input id="mname" className="input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Retail midday scan" data-testid="master-name" />
          </div>
          <div>
            <span className="label flex items-center gap-1.5"><Store className="w-4 h-4" /> Catalogue</span>
            <div className="flex gap-2">
              {(data?.catalogues || [{ key: "retail", label: "Retail Prices" }, { key: "subsidised", label: "Subsidised Prices" }]).map((c) => (
                <button key={c.key} onClick={() => setCatalogue(c.key)}
                  className={`flex-1 min-h-[44px] rounded-card border text-sm ${catalogue === c.key ? "bg-teal text-white border-teal" : "border-stone-border text-ink"}`}
                  data-testid={`master-cat-${c.key}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="mhour">Hour</label>
              <select id="mhour" className="input" value={hour} onChange={(e) => setHour(Number(e.target.value))} data-testid="master-hour">
                {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="mmin">Minute</label>
              <select id="mmin" className="input" value={minute} onChange={(e) => setMinute(Number(e.target.value))} data-testid="master-minute">
                {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
              </select>
            </div>
          </div>
          <div>
            <span className="label">Days</span>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d, i) => (
                <button key={d} onClick={() => toggleDay(i)}
                  className={`min-h-[44px] px-3 rounded-card border text-sm ${weekdays.includes(i) ? "bg-teal text-white border-teal" : "border-stone-border text-ink"}`}
                  data-testid={`master-day-${i}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={create} disabled={busy === "create"} data-testid="master-create">
              {busy === "create" ? <Spinner /> : <Check className="w-4 h-4" />} Create
            </button>
            <button className="btn-ghost flex-1" onClick={() => setShowAdd(false)} data-testid="master-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
